<script setup lang="ts">
/**
 * The sign-in beacon: a dot at the center of three rings, pinging slowly outward.
 *
 * The rings are the product's picture of itself — a program at the center and the
 * clinicians within reach of it — and the ping is the product owner's direction
 * (2026-09-26): not a static graphic but "a slow ping indicating location". It is
 * the one looping motion DESIGN.md section 7 sanctions, and only here.
 *
 * **Decorative, and hidden from assistive technology.** It says nothing a screen
 * reader user is missing.
 *
 * **One ping at a time, slowly, then a rest.** The first version sent two pings
 * half a cycle apart, and the product owner found it too fast: "more spaced out
 * and slower". Each ping now spreads over the first 70% of `--motion-beacon` (6s)
 * and the rest of the cycle is still. The ping is a filled disc scaled up from the
 * dot rather than a ring whose size is animated: `transform` and `opacity` are the
 * two properties a browser can animate without layout, and a scaled border would
 * thicken as it grew.
 *
 * **Reduced motion stops it and leaves nothing behind.** `styles/base.css`
 * collapses every animation to a single 1ms iteration under
 * `prefers-reduced-motion: reduce`. The ping discs rest at `opacity: 0`, so a
 * collapsed animation ends where it started — invisible — and the dot and rings
 * stay, still. The rule below says so explicitly rather than leaning on that.
 *
 * Geometry is in multiples of the dot, so one `size` scales the whole figure and
 * no pixel value appears here (DESIGN.md section 1).
 */

withDefaults(defineProps<{ size?: 'md' | 'lg' }>(), { size: 'md' })
</script>

<template>
  <div
    class="mp-beacon"
    :class="`mp-beacon--${size}`"
    aria-hidden="true"
  >
    <span class="mp-beacon__ring mp-beacon__ring--3" />
    <span class="mp-beacon__ring mp-beacon__ring--2" />
    <span class="mp-beacon__ring mp-beacon__ring--1" />
    <span class="mp-beacon__ping" />
    <span class="mp-beacon__dot" />
  </div>
</template>

<style scoped>
.mp-beacon {
  --beacon-dot: var(--space-6);

  position: relative;
  inline-size: var(--beacon-dot);
  block-size: var(--beacon-dot);
  pointer-events: none;
}

.mp-beacon--lg {
  --beacon-dot: var(--space-8);
}

/* Every part is centered on the dot. */
.mp-beacon > span {
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: 50%;
  border-radius: var(--radius-pill);
  translate: -50% -50%;
}

.mp-beacon__ring {
  border: 1px solid var(--text-on-nav);
}

/* Fainter as they get further out, as in the design. */
.mp-beacon__ring--1 {
  inline-size: calc(var(--beacon-dot) * 5);
  block-size: calc(var(--beacon-dot) * 5);
  opacity: 0.16;
}

.mp-beacon__ring--2 {
  inline-size: calc(var(--beacon-dot) * 10);
  block-size: calc(var(--beacon-dot) * 10);
  opacity: 0.12;
}

.mp-beacon__ring--3 {
  inline-size: calc(var(--beacon-dot) * 15);
  block-size: calc(var(--beacon-dot) * 15);
  opacity: 0.1;
}

.mp-beacon__dot {
  inline-size: var(--beacon-dot);
  block-size: var(--beacon-dot);
  background: var(--accent-beacon);
  box-shadow: 0 0 0 calc(var(--beacon-dot) / 4) color-mix(in srgb, var(--accent-beacon) 22%, transparent);
}

.mp-beacon__ping {
  inline-size: var(--beacon-dot);
  block-size: var(--beacon-dot);
  background: color-mix(in srgb, var(--accent-beacon) 45%, transparent);
  opacity: 0;
  animation: mp-beacon-ping var(--motion-beacon) linear infinite;
}

/* Out to the middle ring over 70% of the cycle, easing off and fading as it
   goes, then still until the next. Starting just outside the halo keeps the ping
   from looking like the dot itself swelling. */
@keyframes mp-beacon-ping {
  0% {
    opacity: 0.55;
    scale: 1.5;
    animation-timing-function: cubic-bezier(0.1, 0.6, 0.3, 1);
  }

  70% {
    opacity: 0;
    scale: 10;
  }

  100% {
    opacity: 0;
    scale: 10;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mp-beacon__ping {
    animation: none;
  }
}
</style>
